import React, { useState } from 'react';
import { Header } from '../Header';
import { useDispatch, useSelector } from 'react-redux';
import { selectRegistration } from '../../store/registration/registration.selector';
import { Error } from '../Error';
import { Loading } from '../Loading';
import { selectUser } from '../../store/auth/auth.selector';
import { logOut } from '../../store/auth/actions/logOut';
import { makeStyles } from '@material-ui/core';
import { Title } from '../Title';
import { Container } from '../helpers/Container';
import { Card, CardBody } from '../Card';
import { Colors } from '../../constants/colors';
import { Button } from '../buttons/Button';

import { Check } from '@material-ui/icons';
import { register } from '../../store/registration/actions/register';
import { getApiErrorMessage } from '../../hooks/getApiErrorMessage';
import { useMountEffect } from '../../hooks/useMountEffect';
import { RequestType } from '../../model/enums/RequestType';
import { useNavigate } from 'react-router-dom';

const useStyles = makeStyles({
  card: {
    textAlign: 'center',
    maxWidth: '100% !important',
    marginTop: 70
  },
  cardBody: {
    justifyContent: 'flex-start !important',
    alignItems: 'center'
  },
  colorDanger: {
    color: Colors.danger
  },
  colorSuccess: {
    color: Colors.success
  },
  paragraph: {
    color: Colors.dark,
    lineHeight: 2
  },
  button: {
    marginTop: 60
  },
  stateBody: {
    marginTop: 30,
    marginBottom: 30
  },
  registrationId: {
    padding: '10px 15px',
    borderColor: Colors.grey,
    borderWidth: 1,
    borderStyle: 'solid',
    background: Colors.background,
    borderRadius: 6,
    minWidth: '300px',
    fontFamily: 'monospace',
    fontSize: 22,
    fontWeight: 700
  },
  successIcon: {
    marginLeft: 20,
    width: 40,
    height: 40,
    position: 'relative',
    top: 5
  }
});

export const RegistrationDone = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const user = useSelector(selectUser);
  const registrationState = useSelector(selectRegistration);

  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [registrationId, setRegistrationId] = useState<string>('');

  /* Send registration when component is mounted */
  useMountEffect(async () => {
    if (!user) {
      await dispatch(logOut());
      navigate('/login', { replace: true });
      return;
    }

    setLoading(true);
    try {
      const id = await register(registrationState, user.requestType);

      if (id) {
        setRegistrationId(id);
      }

      // Logout user to allow sending one form per one sms
      // Refreshing the page will redirect back to login
      await dispatch(logOut());

    } catch (e) {
      setErrorMessage(getApiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  });

  const handleBackToFormClick = () => {
    navigate('/registration', { replace: true });
  };

  return (<>
    <Header showLogout={true} />
    <Container>
      <Card className={classes.card}>
        <CardBody className={classes.cardBody}>
          {(() => {
            if (loading) {
              return <>
                <Title>Probíhá odeslání formuláře...</Title>
                <Loading show={loading} />
              </>;
            } else if (errorMessage.length) {
              return <>
                <Title className={classes.colorDanger}>Chyba odeslání formuláře</Title>
                <p className={classes.paragraph}>Je nám líto, ale při odeslání objednávkového formuláře nastala chyba:</p>
                <div className={classes.stateBody}>
                  <Error errorMessage={errorMessage} />
                </div>
                <Button onClick={handleBackToFormClick}
                        className={classes.button}>Vrátit se k formuláři</Button>
              </>;
            } else {
              return <>
                <Title className={classes.colorSuccess}>
                  Formulář byl úspěšně odeslán
                  <Check fontSize='large'
                         className={classes.successIcon} />
                </Title>
                <p className={classes.paragraph}>
                  {user?.requestType === RequestType.DoctorRegistration && 'Úspěšně jsme přijali Vaší žádost o objednání pacienta.'}
                  {user?.requestType === RequestType.PatientRegistration && 'Úspěšně jsme přijali Vaší žádost o objednání.'}
                  {registrationId.length && <><br /> Číslo odeslané žádosti:</>}
                </p>
                {registrationId.length && <div className={classes.stateBody}>
                  <div className={classes.registrationId}>{registrationId}</div>
                </div>}
                <p className={classes.paragraph}>
                  {registrationId.length && <>Číslo odeslané žádosti jsme Vám také zaslali na e-mail.<br /></>}
                  <strong>Vyčkejte, prosím, na telefonické kontaktování ze strany IKEM.</strong>
                </p>
                <p className={classes.paragraph}>
                  Děkujeme.
                </p>
              </>;
            }
          })()}
        </CardBody>
      </Card>
    </Container>
  </>);
};
